import React, { useState, useEffect, useCallback } from 'react'
import api from '../services/api.ts'
import { Link, useNavigate } from 'react-router-dom'
import ShareReportDialog from '../components/ShareReportDialog.tsx'
import Pagination, { PaginationInfo } from '../components/Pagination.tsx'
import { TableHeader, TableContainer } from '../components/Skeleton.tsx'
import { useColumnResize } from '../hooks/useColumnResize.ts'

interface Report {
  id: string
  name: string
  cadence: string
  company_id: string | null
  companyName: string | null
  createdBy: { id: string; name: string }
  shareCount: number
  config_json: any
  created_at: string
  updated_at: string
}

interface Company {
  id: string
  name: string
}

const Reports: React.FC = () => {
  const navigate = useNavigate()
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [shareDialogReport, setShareDialogReport] = useState<Report | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  // Filters
  const [companies, setCompanies] = useState<Company[]>([])
  const [selectedCompany, setSelectedCompany] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const { widths, startResize } = useColumnResize('reports-col-widths')

  // Pagination
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  })

  // Load companies for filter dropdown
  useEffect(() => {
    api
      .get('/api/user/companies')
      .then(res => {
        setCompanies(res.data.companies || [])
      })
      .catch(() => {
        setError('Failed to load companies. Please refresh and try again.')
      })
  }, [])

  const fetchReports = useCallback(
    async (page: number) => {
      setLoading(true)
      setError('')
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: '25',
          sortBy,
          sortOrder,
        })
        if (selectedCompany) params.set('companyId', selectedCompany)
        if (searchQuery) params.set('search', searchQuery)

        const res = await api.get(`/api/reports?${params}`)
        setReports(res.data.data || [])
        setPagination(res.data.pagination)
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load reports')
      } finally {
        setLoading(false)
      }
    },
    [selectedCompany, searchQuery, sortBy, sortOrder],
  )

  // Fetch page 1 when filters change
  useEffect(() => {
    fetchReports(1)
  }, [fetchReports])

  // Fetch specific page when pagination changes (from user click)
  const handlePageChange = useCallback(
    (page: number) => {
      fetchReports(page)
    },
    [fetchReports],
  )

  const handleDelete = async (reportId: string) => {
    try {
      await api.delete(`/api/reports/${reportId}`)
      setDeleteConfirm(null)
      fetchReports(pagination.page)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete report')
    }
  }

  const getCadenceColor = (cadence: string) => {
    switch (cadence) {
      case 'daily':
        return 'bg-blue-100 text-blue-800'
      case 'weekly':
        return 'bg-green-100 text-green-800'
      case 'monthly':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  const clearFilters = () => {
    setSelectedCompany('')
    setSearchQuery('')
    setSortBy('created_at')
    setSortOrder('desc')
  }

  const hasActiveFilters =
    selectedCompany || searchQuery || sortBy !== 'created_at' || sortOrder !== 'desc'

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Reports</h1>
            <p className="text-gray-600">
              Manage and share performance reports across your companies
            </p>
          </div>
          <Link
            to="/setup-report"
            className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            New Report
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Company filter */}
            <select
              value={selectedCompany}
              onChange={e => setSelectedCompany(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
            >
              <option value="">All Companies</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by report name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Sort buttons */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
              <button
                onClick={() => toggleSort('created_at')}
                className={`px-3 py-2 ${sortBy === 'created_at' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                Date {sortBy === 'created_at' && (sortOrder === 'desc' ? '↓' : '↑')}
              </button>
              <button
                onClick={() => toggleSort('name')}
                className={`px-3 py-2 border-l border-gray-300 ${sortBy === 'name' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                Name {sortBy === 'name' && (sortOrder === 'desc' ? '↓' : '↑')}
              </button>
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && reports.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-500">Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          /* Empty state */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {hasActiveFilters ? 'No reports match your filters' : 'No reports yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {hasActiveFilters
                ? 'Try adjusting your filters or search query'
                : 'Get started by creating your first report'}
            </p>
            {!hasActiveFilters && (
              <Link
                to="/setup-report"
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Create Your First Report
              </Link>
            )}
          </div>
        ) : (
          <TableContainer
            loading={loading}
            hasData={reports.length > 0}
            skeletonRows={8}
            skeletonCols={7}
            emptyMessage=""
          >
            {/* Report table */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <table
                className={`min-w-full divide-y divide-gray-200 ${Object.keys(widths).length > 0 ? 'table-fixed' : ''}`}
              >
                <TableHeader
                  columns={[
                    { label: 'Report', key: 'name' },
                    { label: 'Company', key: 'company', sortable: false },
                    { label: 'Created By', key: 'created_by', sortable: false },
                    { label: 'Cadence', key: 'cadence' },
                    { label: 'Shares', key: 'shares', sortable: false },
                    { label: 'Created', key: 'created_at' },
                    { label: 'Actions', key: 'actions', sortable: false },
                  ]}
                  sortBy={sortBy}
                  sortDir={sortOrder}
                  onSort={toggleSort}
                  widths={widths}
                  onResize={startResize}
                />
                <tbody className="divide-y divide-gray-200">
                  {reports.map(report => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {report.name}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-600">{report.companyName || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-500">{report.createdBy?.name || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCadenceColor(report.cadence)}`}
                        >
                          {report.cadence}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-500">{report.shareCount}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-500">
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setShareDialogReport(report)}
                            className="px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          >
                            Share
                          </button>
                          <button
                            onClick={() => navigate(`/setup-report/${report.id}`)}
                            className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          >
                            Edit
                          </button>
                          {deleteConfirm === report.id ? (
                            <>
                              <button
                                onClick={() => handleDelete(report.id)}
                                className="px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(report.id)}
                              className="px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-4">
                <Pagination {...pagination} onPageChange={handlePageChange} />
              </div>
            )}
          </TableContainer>
        )}
      </div>

      {/* Share Dialog */}
      {shareDialogReport && (
        <ShareReportDialog
          reportId={shareDialogReport.id}
          reportName={shareDialogReport.name}
          onClose={() => {
            setShareDialogReport(null)
            fetchReports(pagination.page)
          }}
        />
      )}
    </div>
  )
}

export default Reports
